const FULL_GAME_PERIODS = new Set([null, undefined, '', 'Game'])
const COMPACT_BET_TYPES = new Set(['Moneyline', 'Spread', 'Run Line', 'Puck Line', 'Total'])

function normalizedPeriod(value) {
  if (value === null || value === undefined || value === '') return 'Game'
  return /^(game|full game|match)$/i.test(String(value).trim()) ? 'Game' : String(value).trim()
}

function finiteNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string' || value.trim() === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function rounded(value) {
  return Number.isFinite(value) ? Number(value.toFixed(10)) : null
}

export function americanToDecimal(american) {
  const value = finiteNumber(american)
  if (value === null || Math.abs(value) < 100) return null
  if (value >= 100) return 1 + value / 100
  return 1 + 100 / Math.abs(value)
}

export function americanToImplied(american) {
  const value = finiteNumber(american)
  if (value === null || Math.abs(value) < 100) return null
  if (value >= 100) return 100 / (value + 100)
  return Math.abs(value) / (Math.abs(value) + 100)
}

export function impliedToAmerican(implied) {
  const value = finiteNumber(implied)
  if (value === null || value <= 0 || value >= 1) return null
  if (value <= 0.5) return rounded(100 * (1 - value) / value)
  return rounded(-100 * value / (1 - value))
}

// This is the market's mean implied probability expressed as American odds.
// It is intentionally not a de-vigged fair price.
export function marketAverageAmerican(rows) {
  const implied = rows
    .map((row) => americanToImplied(row?.american))
    .filter((value) => value !== null)
  if (implied.length === 0) return null
  return impliedToAmerican(implied.reduce((sum, value) => sum + value, 0) / implied.length)
}

function scalarToken(value) {
  if (value === null) return 'null'
  if (value === undefined) return 'undefined'
  return String(value).replaceAll('%', '%25').replaceAll('|', '%7C')
}

function compareScalar(left, right) {
  if (left === right) return 0
  if (left === null || left === undefined) return -1
  if (right === null || right === undefined) return 1
  const a = finiteNumber(left)
  const b = finiteNumber(right)
  if (a !== null && b !== null && a !== b) return a - b
  const x = String(left)
  const y = String(right)
  return x < y ? -1 : x > y ? 1 : 0
}

function compareTuples(left, right) {
  for (let index = 0; index < Math.max(left.length, right.length); index += 1) {
    const result = compareScalar(left[index], right[index])
    if (result !== 0) return result
  }
  return 0
}

function rowSortTuple(row) {
  return [row.book, row.state, row.pid, row.dl, row.american, row.prev]
}

function exactKey(row) {
  return [row.league, row.event, row.et_date, normalizedPeriod(row.period), row.bet_type, row.selection, row.line]
    .map(scalarToken)
    .join('|')
}

function groupSortTuple(group) {
  return [
    group.league,
    group.et_date,
    group.et_time,
    group.event,
    group.bet_type,
    group.selection,
    group.line,
  ]
}

export function selectBestOffer(rows) {
  const candidates = rows
    .map((row) => ({ row, decimal: americanToDecimal(row?.american) }))
    .filter(({ decimal }) => decimal !== null)
    .sort((left, right) => {
      if (left.decimal !== right.decimal) return right.decimal - left.decimal
      return compareTuples(rowSortTuple(left.row), rowSortTuple(right.row))
    })
  return candidates[0]?.row ?? null
}

function optionValues(value) {
  if (value === null || value === undefined || value === '' || /^all$/i.test(String(value))) return null
  const source = value instanceof Set ? [...value] : Array.isArray(value) ? value : [value]
  if (source.length === 0) return null
  return new Set(source.map((item) => String(item).toLowerCase()))
}

function matchesOption(value, requested) {
  return requested === null || requested.has(String(value ?? '').toLowerCase())
}

export function filterOfferRows(rows, filters = {}) {
  const leagues = optionValues(filters.league)
  const categories = optionValues(filters.category)
  const betTypes = optionValues(filters.betType)
  const books = optionValues(filters.books)
  const states = optionValues(filters.state)
  const query = String(filters.search ?? '').trim().toLowerCase()

  return rows.filter((row) => {
    if (!matchesOption(row.league, leagues)) return false
    if (!matchesOption(row.category, categories)) return false
    if (!matchesOption(row.bet_type, betTypes)) return false
    if (!matchesOption(row.book, books)) return false
    if (!matchesOption(row.state, states)) return false
    if (!query) return true
    return [
      row.league, row.category, row.event, row.et_date, row.et_time, row.period,
      row.bet_type, row.selection, row.line, row.book, row.american, row.state,
    ].some((value) => String(value ?? '').toLowerCase().includes(query))
  })
}

export function groupExactOffers(rows) {
  const buckets = new Map()
  for (const row of rows) {
    const key = exactKey(row)
    if (!buckets.has(key)) buckets.set(key, [])
    buckets.get(key).push(row)
  }

  const groups = [...buckets.entries()].map(([key, offerRows]) => {
    const orderedRows = [...offerRows].sort((a, b) => compareTuples(rowSortTuple(a), rowSortTuple(b)))
    const first = orderedRows[0]
    const times = orderedRows.map((row) => row.et_time).filter((value) => value !== null && value !== undefined)
    times.sort(compareScalar)
    return {
      key,
      league: first.league,
      event: first.event,
      et_date: first.et_date,
      et_time: times[0] ?? null,
      period: normalizedPeriod(first.period),
      bet_type: first.bet_type,
      selection: first.selection,
      line: first.line,
      rows: orderedRows,
      bestOffer: selectBestOffer(orderedRows),
      marketAverageAmerican: marketAverageAmerican(orderedRows),
    }
  })

  return groups.sort((a, b) => compareTuples(groupSortTuple(a), groupSortTuple(b)))
}

export function countExactOfferGroups(rows) {
  const keys = new Set()
  for (const row of rows) keys.add(exactKey(row))
  return keys.size
}

function compactFamilyKey(row) {
  return [row.league, row.event, row.et_date, row.book, row.bet_type, row.selection]
    .map(scalarToken)
    .join('|')
}

function choosePrimaryRows(rows) {
  const families = new Map()
  for (const row of rows) {
    const key = compactFamilyKey(row)
    if (!families.has(key)) families.set(key, [])
    families.get(key).push(row)
  }
  const selected = []
  for (const family of families.values()) {
    const explicit = family.filter((row) => row.is_primary === true)
    const unmarked = family.filter((row) => row.is_primary === null || row.is_primary === undefined)
    const candidates = explicit.length > 0 ? explicit : unmarked
    candidates.sort((a, b) => compareTuples([a.line, ...rowSortTuple(a)], [b.line, ...rowSortTuple(b)]))
    if (candidates[0]) selected.push(candidates[0])
  }
  return selected
}

export function buildCompactGameCards(rows) {
  const eligible = rows.filter((row) => FULL_GAME_PERIODS.has(row.period) && COMPACT_BET_TYPES.has(row.bet_type))
  const selectedRows = choosePrimaryRows(eligible)
  const cardBuckets = new Map()
  for (const group of groupExactOffers(selectedRows)) {
    const key = [group.league, group.event, group.et_date].map(scalarToken).join('|')
    if (!cardBuckets.has(key)) cardBuckets.set(key, [])
    cardBuckets.get(key).push(group)
  }
  const cards = [...cardBuckets.entries()].map(([key, offerGroups]) => {
    const first = offerGroups[0]
    return {
      key,
      league: first.league,
      event: first.event,
      et_date: first.et_date,
      et_time: first.et_time,
      offerGroups,
    }
  }).sort((a, b) => compareTuples([a.league, a.et_date, a.et_time, a.event], [b.league, b.et_date, b.et_time, b.event]))

  return {
    cards,
    sourceRowCount: rows.length,
    eligibleRowCount: eligible.length,
    selectedRowCount: selectedRows.length,
    omittedAlternativeCount: eligible.length - selectedRows.length,
    excludedNonGameLineCount: rows.length - eligible.length,
    proofLedger: false,
  }
}

function categoryForRow(row) {
  if (row.category !== null && row.category !== undefined && row.category !== '') return row.category
  return COMPACT_BET_TYPES.has(row.bet_type) ? 'Game Lines' : 'Other'
}

export function buildAvailability(categoryManifest = [], rows = []) {
  const entries = new Map()
  for (const item of categoryManifest) {
    const league = item.league ?? item.sport ?? null
    const key = `${scalarToken(league)}|${scalarToken(item.category)}`
    entries.set(key, { ...item, league, category: item.category })
  }
  for (const row of rows) {
    const category = categoryForRow(row)
    const key = `${scalarToken(row.league)}|${scalarToken(category)}`
    if (!entries.has(key)) entries.set(key, { league: row.league, category })
  }

  return [...entries.values()].map((entry) => {
    const rowCount = rows.filter((row) => {
      const leagueMatches = entry.league === null || entry.league === undefined || row.league === entry.league
      return leagueMatches && categoryForRow(row) === entry.category
    }).length
    return { ...entry, rowCount, available: rowCount > 0, status: rowCount > 0 ? 'active' : 'empty' }
  }).sort((a, b) => compareTuples([a.league, a.category], [b.league, b.category]))
}

function sortedUnique(values) {
  return [...new Set(values.filter((value) => value !== null && value !== undefined))].sort(compareScalar)
}

export function summarizeRows(rows) {
  const stateCounts = {}
  for (const row of rows) {
    const state = row.state ?? 'unknown'
    stateCounts[state] = (stateCounts[state] ?? 0) + 1
  }
  return {
    rowCount: rows.length,
    exactOfferCount: countExactOfferGroups(rows),
    eventCount: new Set(rows.map((row) => [row.league, row.event, row.et_date].map(scalarToken).join('|'))).size,
    bookCount: new Set(rows.map((row) => row.book)).size,
    pricedRowCount: rows.filter((row) => finiteNumber(row.american) !== null).length,
    unpricedRowCount: rows.filter((row) => finiteNumber(row.american) === null).length,
    leagues: sortedUnique(rows.map((row) => row.league)),
    categories: sortedUnique(rows.map(categoryForRow)),
    betTypes: sortedUnique(rows.map((row) => row.bet_type)),
    books: sortedUnique(rows.map((row) => row.book)),
    stateCounts,
  }
}
